import { useLayoutEffect, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { Language } from "../../shared/types/websiteTypes";
import DOMPurify from "dompurify";
import NotificationPreview from "../NotificationPreview";

import { useSelector } from "react-redux";
import { getCustomWebLogo } from "../../services/selectors/web-selectors";

interface BrandingEditNotificationProps {
  icon?: boolean;
  textColor?: string;
  iconColor?: string;
  backgroundColor?: string;
  borderRadius?: number;
  backgroundOpacity?: number;
  iconBackgroundColor?: string;
  iconBackgroundRadius?: number;
  activeSize: "classic" | "small" | "compact";
}

export const BrandingEditNotification: FC<BrandingEditNotificationProps> = ({
  icon = true,
  textColor,
  iconColor,
  backgroundColor,
  borderRadius,
  backgroundOpacity,
  iconBackgroundColor,
  iconBackgroundRadius,
  activeSize,
}) => {
  const { t, i18n } = useTranslation();
  const customWebBranding = useSelector(getCustomWebLogo);

  const [notificationHtml, setNotificationHtml] = useState<string>("");

  useLayoutEffect(() => {
    const createNotificationPreviewHtml = async () => {
      const result = await window.getNotificationPreview(
        {
          campaign: {
            branding: {
              textColor: textColor,
              backgroundColor: backgroundColor,
              borderRadius: borderRadius,
              backgroundOpacity: backgroundOpacity,
              iconColor: iconColor,
              iconBackgroundColor: iconBackgroundColor,
              iconBackgroundRadius: iconBackgroundRadius,
              activeSize: activeSize,
              brandingIsShowingIcon: typeof icon === "boolean" ? icon : true,
              logoDisplay: customWebBranding?.logoDisplay,
              logo: customWebBranding?.logo,
              logoBackgroundColor: customWebBranding?.logoBackgroundColor,
              logoText: customWebBranding?.logoText,
            },
            notificationVisualSettings: {
              roundness: 1,
            },
          },
          settings: {
            type: "lastOrders",
            notificationVisualSettings: {
              backgroundColor: backgroundColor ?? "",
              textColor: textColor ?? "",
              iconBackgroundColor: iconBackgroundColor ?? "",
              iconColor: iconColor ?? "",
              icon: "",
              roundness: borderRadius ?? 0,
              linkTextColor: "",
              linkButtonBackgroundColor: "",
              backgroundOpacity: backgroundOpacity ?? 1,
              activeSize: activeSize ?? "classic",
              isShowingIcon: typeof icon === "boolean" ? icon : true,
              iconBackgroundRadius: iconBackgroundRadius ?? 0,
            },
          },
        },
        "lastOrders",
        t("campaign.notifications.lastOrders.previewText1") ?? "",
        t("campaign.notifications.lastOrders.previewText2") ?? "",
        undefined,
        i18n.language as Language,
      );

      // Transforms object htmldivelement to pure string
      const cleanHtmlString = DOMPurify.sanitize(result, {
        USE_PROFILES: { html: true, svg: true },
      });
      setNotificationHtml(cleanHtmlString);
    };

    createNotificationPreviewHtml();
  }, [
    iconBackgroundColor,
    textColor,
    backgroundColor,
    borderRadius,
    backgroundOpacity,
    iconBackgroundRadius,
    iconColor,
    icon,
    activeSize,
    t,
    i18n.language,
    customWebBranding,
  ]);

  return <NotificationPreview notificationHtml={notificationHtml} />;
};
